import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { debugLog } from '../utils/logger';
import { useTranslation } from '../i18n';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface SubscriptionPageProps {
  userInfo: UserInfo;
}

export function SubscriptionPage({ userInfo }: SubscriptionPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Use userInfo to display user-specific information if needed
  useEffect(() => {
    if (userInfo) {
      debugLog(`User ${userInfo.username} viewing subscription options with balance: ${userInfo.balance}`);
    }
  }, [userInfo]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContactSupport = () => {
    // Open the spr_team Telegram account
    window.open('https://t.me/spr_team', '_blank');
  };

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-[100] shadow-[0_4px_12px_-6px_rgba(0,0,0,0.1)]" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleBack}
              className="p-2 -ml-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
            >
              <span className="material-symbols-outlined text-theme-text">arrow_back</span>
            </button>
            <h1 className="text-2xl font-bold text-theme-text/90">
              {t('subscriptionPage.headerTitle')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-[88px] pb-24">
        <div className="text-center mb-8">
          <div className="text-xl font-medium text-theme-text mb-2">
            {t('subscriptionPage.subtitle')}
          </div>
          <div className="text-theme-hint mb-4">
            {t('subscriptionPage.subtitleHint')}
          </div>
        </div>

        <div className="space-y-6">
          {/* Pay As You Go Option */}
          <div 
            className={`relative w-full p-5 rounded-2xl border border-[#2D62FF] bg-[#2D62FF]/10 transition-all`}
          >
            <div className="absolute top-3 right-3">
              <span className="material-symbols-outlined text-[#2D62FF]">check_circle</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">💰</span>
                <span className="font-bold text-lg text-theme-text">{t('subscriptionPage.payAsYouGo.name')}</span>
              </div>
              <ul className="text-theme-hint mb-3 list-disc pl-5 space-y-1">
                <li>{t('subscriptionPage.payAsYouGo.feature1')}</li>
                <li>{t('subscriptionPage.payAsYouGo.feature2')}</li>
              </ul>
              <div className="bg-[#2D62FF]/10 text-[#2D62FF] text-sm font-medium px-3 py-1.5 rounded-full self-start">
                {t('subscriptionPage.payAsYouGo.currentPlanBadge')}
              </div>
            </div>
          </div>

          {/* Pro Option */}
          <div 
            className={`relative w-full p-5 rounded-2xl border border-white/10 bg-white/5 transition-all`}
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">⭐</span>
                <span className="font-bold text-lg text-theme-text">{t('subscriptionPage.pro.name')}</span>
              </div>
              <ul className="text-theme-hint mb-3 list-disc pl-5 space-y-1">
                <li>{t('subscriptionPage.pro.feature1')}</li>
                <li>{t('subscriptionPage.pro.feature2')}</li>
                <li>{t('subscriptionPage.pro.feature3')}</li>
              </ul>
              <button 
                onClick={handleContactSupport}
                className="bg-[#2D62FF] text-white font-medium py-2 px-4 rounded-lg self-start hover:bg-[#2D62FF]/90 transition-colors"
              >
                {t('subscriptionPage.contactSupportButton')}
              </button>
            </div>
          </div>

          {/* Enterprise Option */}
          <div 
            className={`relative w-full p-5 rounded-2xl border border-white/10 bg-white/5 transition-all`}
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🏢</span>
                <span className="font-bold text-lg text-theme-text">{t('subscriptionPage.enterprise.name')}</span>
              </div>
              <ul className="text-theme-hint mb-3 list-disc pl-5 space-y-1">
                <li>{t('subscriptionPage.enterprise.feature1')}</li>
                <li>{t('subscriptionPage.enterprise.feature2')}</li>
                <li>{t('subscriptionPage.enterprise.feature3')}</li>
              </ul>
              <button 
                onClick={handleContactSupport}
                className="bg-[#2D62FF] text-white font-medium py-2 px-4 rounded-lg self-start hover:bg-[#2D62FF]/90 transition-colors"
              >
                {t('subscriptionPage.contactSupportButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 